import { Ref } from 'react';
import { useTranslation } from 'react-i18next';

type UseInputTextParams = {
  disabled?: boolean;
  icon?: string;
  iconAriaLabel?: string;
  onClickIcon?: (...args: unknown[]) => void;
  onClickSecondaryIcon?: (...args: unknown[]) => void;
  ref?: Ref<HTMLInputElement>;
  referer?: Ref<HTMLInputElement>;
  secondaryIcon?: string;
  secondaryIconAriaLabel?: string;
};

function useInputText({
  disabled,
  icon,
  iconAriaLabel,
  onClickIcon,
  onClickSecondaryIcon,
  ref,
  referer,
  secondaryIcon,
  secondaryIconAriaLabel,
}: UseInputTextParams) {
  const { t } = useTranslation();

  const refInput = referer ?? ref;

  const getIconAriaLabel = (iconType: string): string => {
    const iconLabels: Record<string, string> = {
      visible: t('accessibility:Show password'),
      'visible-off': t('accessibility:Hide password'),
      search: t('accessibility:Search'),
      clear: t('accessibility:Clear field'),
      close: t('accessibility:Clear field'),
      calendar: t('accessibility:Open calendar'),
      eye: t('accessibility:Show password'),
      'eye-off': t('accessibility:Hide password'),
    };

    return iconLabels[iconType] || t('accessibility:Click {{icon}} icon', { icon: iconType });
  };

  const getIconAriaLabelValue = icon ? (iconAriaLabel || getIconAriaLabel(icon)) : undefined;

  const isIconClickable = typeof onClickIcon === 'function' && !disabled;

  const getSecondaryIconAriaLabelValue = secondaryIcon
    ? (secondaryIconAriaLabel || getIconAriaLabel(secondaryIcon))
    : undefined;

  const isSecondaryIconClickable = typeof onClickSecondaryIcon === 'function' && !disabled;

  return {
    refInput,
    getIconAriaLabelValue,
    isIconClickable,
    getSecondaryIconAriaLabelValue,
    isSecondaryIconClickable,
  };
}

export default useInputText;
